import { motion } from 'framer-motion'
import { useOnboardingStore } from '../store/onboardingStore'
import { Button } from '../components/Button'
import { calculateNutrientLimits } from '../lib/nutrients'
import { HeaderWash, V2, V2Icon, KIDNEY_STATUS_V2_ICONS, PRIMARY_GOAL_V2_ICONS } from '../components/v2'
import { usesAppLook } from '../lib/variant'
import type { KidneyStatus, PrimaryGoal } from '../types/onboarding'

/** organic-v2 nutrient chip: the site's existing nutrient art (also used by MealAnalysisResult). */
function V2NutrientMark({ nutrient }: { nutrient: 'sodium' | 'potassium' | 'phosphorus' | 'protein' }) {
  return <img src={`/images/nutrients/${nutrient}.png`} alt="" className="w-5 h-5 object-contain" />
}

const kidneyStatusLabels: Record<string, string> = {
  stage_1: 'CKD Stage 1',
  stage_2: 'CKD Stage 2',
  stage_3a: 'Stage 3a CKD',
  stage_3b: 'Stage 3b CKD',
  stage_4: 'CKD Stage 4',
  stage_5: 'CKD Stage 5',
  on_dialysis: 'Dialysis',
  kidney_stones: 'Kidney Stones',
  no_diagnosis: 'Monitoring',
  not_sure: 'General Kidney Health',
}

const kidneyStatusDescriptions: Record<string, string> = {
  stage_1: 'Mild sodium and phosphorus awareness',
  stage_2: 'Mild sodium and phosphorus awareness',
  stage_3a: 'Moderate sodium, potassium, and phosphorus limits',
  stage_3b: 'Moderate sodium, potassium, and phosphorus limits',
  stage_4: 'Strict sodium, potassium, and phosphorus limits',
  stage_5: 'Strict nutrient management with fluid restriction',
  on_dialysis: 'Nutrient management around dialysis treatments',
  kidney_stones: 'Focus on hydration and oxalate management',
  no_diagnosis: 'General kidney-healthy eating guidelines',
  not_sure: 'General kidney-healthy eating guidelines',
}

const goalLabels: Record<string, { title: string; desc: string; icon: string }> = {
  manage_ckd: { title: 'Managing CKD Daily', desc: 'Helping you understand kidney-friendly eating', icon: '🫘' },
  slow_progression: { title: 'Slowing Progression', desc: 'Optimizing your diet to protect kidney function', icon: '🛡️' },
  dialysis_support: { title: 'Dialysis Nutrition', desc: 'Balancing nutrients around your treatments', icon: '💧' },
  post_transplant: { title: 'Post-Transplant Care', desc: 'Supporting your new kidney with proper nutrition', icon: '💚' },
  kidney_stones: { title: 'Stone Prevention', desc: 'Helping you avoid foods that trigger stones', icon: '🪨' },
  caregiver: { title: 'Supporting Your Loved One', desc: 'Tools to help you prepare kidney-safe meals', icon: '💜' },
  general_health: { title: 'Proactive Kidney Care', desc: 'Building habits for long-term kidney wellness', icon: '✨' },
  exploring: { title: 'Kidney Health Awareness', desc: 'Learning about kidney-friendly nutrition', icon: '👀' },
}

export function ProfileRevealStep() {
  const { data, goNext } = useOnboardingStore()

  const limits = calculateNutrientLimits(
    data.kidneyStatus,
    data.comorbidities,
    data.weightKg,
    data.dialysisType,
  )

  const stageLabel = kidneyStatusLabels[data.kidneyStatus || 'not_sure'] || 'Kidney Health'
  const stageDesc = kidneyStatusDescriptions[data.kidneyStatus || 'not_sure'] || 'General kidney-healthy eating guidelines'

  const goal = goalLabels[data.primaryGoal || 'general_health'] || goalLabels.general_health

  // organic-v2: app 4.33 look — white cards on the #FAFBFC page, illustrated
  // marks (status + focus reuse the art the visitor tapped, like the app).
  const v2 = usesAppLook()
  const v2Card = 'bg-white border border-slate-200 rounded-2xl p-5 mb-4'
  const v2StatusIcon = KIDNEY_STATUS_V2_ICONS[(data.kidneyStatus || 'not_sure') as KidneyStatus] || 'kidney-not-sure'
  const v2GoalKey = (data.primaryGoal && goalLabels[data.primaryGoal] ? data.primaryGoal : 'general_health') as PrimaryGoal
  const v2GoalIcon = PRIMARY_GOAL_V2_ICONS[v2GoalKey]

  return (
    <div className={v2 ? `min-h-dvh flex flex-col relative ${V2.page}` : 'min-h-dvh flex flex-col bg-white'}>
      {v2 && <HeaderWash />}
      <div className={v2 ? 'flex-1 overflow-y-auto relative z-10' : 'flex-1 overflow-y-auto'}>
        <div className="max-w-[600px] mx-auto px-6 py-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {/* Title */}
            <h1 className={v2 ? `text-2xl font-bold tracking-[-0.25px] ${V2.text} mb-6` : 'text-2xl font-bold text-gray-900 mb-6'}>Your KidneyPal Profile</h1>

            {/* Card 1: Daily Nutrient Limits */}
            <div className={v2 ? v2Card : 'bg-gray-50 rounded-2xl p-5 mb-4'}>
              <div className="flex flex-col items-center mb-4">
                <img src="/images/mascot-clipboard.png" alt="" className="w-14 h-14 object-contain" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-primary mt-1">Your Daily Limits</h3>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Sodium */}
                <div className="rounded-xl p-3" style={{ backgroundColor: 'rgba(245, 158, 11, 0.10)' }}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: 'rgba(245, 158, 11, 0.20)' }}>{v2 ? <V2NutrientMark nutrient="sodium" /> : '🧂'}</span>
                    <span className="font-semibold text-sm text-gray-700">Sodium</span>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: '#F59E0B' }}>
                    {limits.sodium.toLocaleString()}<span className="text-sm font-normal text-gray-400">mg</span>
                  </p>
                </div>

                {/* Potassium */}
                <div className="rounded-xl p-3" style={{ backgroundColor: 'rgba(34, 197, 94, 0.10)' }}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: 'rgba(34, 197, 94, 0.20)' }}>{v2 ? <V2NutrientMark nutrient="potassium" /> : '⚡'}</span>
                    <span className="font-semibold text-sm text-gray-700">Potassium</span>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: '#22C55E' }}>
                    {limits.potassium.toLocaleString()}<span className="text-sm font-normal text-gray-400">mg</span>
                  </p>
                </div>

                {/* Phosphorus */}
                <div className="rounded-xl p-3" style={{ backgroundColor: 'rgba(139, 92, 246, 0.10)' }}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: 'rgba(139, 92, 246, 0.20)' }}>{v2 ? <V2NutrientMark nutrient="phosphorus" /> : '🦴'}</span>
                    <span className="font-semibold text-sm text-gray-700">Phosphorus</span>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: '#8B5CF6' }}>
                    {limits.phosphorus.toLocaleString()}<span className="text-sm font-normal text-gray-400">mg</span>
                  </p>
                </div>

                {/* Protein */}
                <div className="rounded-xl p-3" style={{ backgroundColor: 'rgba(59, 130, 246, 0.10)' }}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: 'rgba(59, 130, 246, 0.20)' }}>{v2 ? <V2NutrientMark nutrient="protein" /> : '💪'}</span>
                    <span className="font-semibold text-sm text-gray-700">Protein</span>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: '#3B82F6' }}>
                    {limits.protein}<span className="text-sm font-normal text-gray-400">g</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2: Daily Fluid Limit */}
            {limits.fluid && (
              <div className={v2 ? v2Card : 'bg-gray-50 rounded-2xl p-5 mb-4'}>
                <div className="flex flex-col items-center mb-4">
                  <img src="/images/mascot-waterdrop.png" alt="" className="w-14 h-14 object-contain" />
                  <h3 className="text-sm font-bold uppercase tracking-wider mt-1" style={{ color: '#0891B2' }}>Daily Fluid Limit</h3>
                </div>

                <div className="flex flex-col items-center">
                  {/* Wave circle */}
                  <div className="w-20 h-20 rounded-full relative overflow-hidden mb-3" style={{ border: '3px solid rgba(8, 145, 178, 0.2)', background: 'linear-gradient(180deg, rgba(8, 145, 178, 0.05) 0%, rgba(8, 145, 178, 0.15) 100%)' }}>
                    <div className="absolute bottom-0 left-0 right-0" style={{ height: '45%', background: 'linear-gradient(180deg, rgba(8, 145, 178, 0.3) 0%, rgba(8, 145, 178, 0.5) 100%)', borderRadius: '40% 40% 0 0' }} />
                    {v2 ? (
                      <span className="absolute inset-0 flex items-center justify-center"><V2Icon name="fluid-water" size={28} /></span>
                    ) : (
                      <span className="absolute inset-0 flex items-center justify-center text-lg">💧</span>
                    )}
                  </div>

                  <p className="text-3xl font-bold" style={{ color: '#0891B2' }}>
                    {limits.fluid.toLocaleString()} <span className="text-base font-normal">ml</span>
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {limits.fluid <= 1500
                      ? 'Fluid restriction recommended'
                      : limits.fluid <= 2000
                        ? 'Moderate fluid intake'
                        : 'Stay well hydrated'}
                  </p>
                </div>
              </div>
            )}

            {/* Card 3: Kidney Status */}
            <div className={v2 ? v2Card : 'bg-gray-50 rounded-2xl p-5 mb-4'}>
              <div className="flex items-center gap-3 mb-2">
                {v2 ? <V2Icon name={v2StatusIcon} size={34} /> : <span className="text-3xl">🏥</span>}
                <div>
                  <p className="text-sm font-semibold text-primary">Your Kidney Status</p>
                  <p className="text-lg font-bold text-gray-900">{stageLabel}</p>
                </div>
              </div>
              <p className="text-sm text-gray-500">{stageDesc}</p>
              <p className="text-xs text-gray-400 mt-2">Limits based on NKF KDOQI Guidelines</p>
            </div>

            {/* Card 4: Personalized Focus */}
            <div className="rounded-2xl p-5 mb-4 border-2 border-amber-300" style={{ backgroundColor: '#FFFBEB' }}>
              <div className="flex items-center gap-3 mb-2">
                {v2 ? <V2Icon name={v2GoalIcon} size={34} /> : <span className="text-3xl">{goal.icon}</span>}
                <div>
                  <p className="text-sm font-semibold text-amber-600">Your Personalized Focus</p>
                  <p className="text-lg font-bold text-gray-900">{goal.title}</p>
                </div>
              </div>
              <p className="text-sm text-amber-700">{goal.desc}</p>
            </div>

            {/* Card 5: Recipe Collection */}
            <div className={v2 ? v2Card : 'bg-gray-50 rounded-2xl p-5 mb-4'}>
              <div className="flex flex-col items-center">
                <img src="/images/mascot-holdingveggies.png" alt="" className="w-14 h-14 object-contain" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-primary mt-1">Your Recipe Collection</h3>
                <p className="text-lg font-bold text-gray-900 mt-2">100+ recipes match your profile</p>
                <p className="text-sm text-gray-500">Filtered for {stageLabel}</p>
              </div>
            </div>

            {/* Card 6: First Mission */}
            <div className="rounded-2xl p-5 mb-6" style={{ backgroundColor: 'rgba(20, 184, 166, 0.08)' }}>
              <div className="flex flex-col items-center">
                <img src="/images/mascot-checkmark.png" alt="" className="w-14 h-14 object-contain" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-primary mt-1">Your First Mission</h3>
                <p className="text-lg font-bold text-gray-900 mt-2">Scan your next meal!</p>
                <p className="text-sm text-gray-500">Let's see how KidneyPal works</p>
              </div>
            </div>

            <Button onClick={() => goNext()}>
              Try a Demo Scan
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
